import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, X } from "lucide-react";

const STATUSES = ["created", "paid", "failed", "shipped", "delivered", "cancelled", "refunded"];

const statusColor = (s) => ({
    paid: "bg-green-100 text-green-700",
    created: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
    shipped: "bg-blue-100 text-blue-700",
    delivered: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-gray-200 text-gray-700",
    refunded: "bg-purple-100 text-purple-700",
}[s] || "bg-gray-100 text-gray-700");

function fmtINR(n) { return `₹${(Number(n || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`; }

function CreateOrderDialog({ open, onOpenChange, onSaved }) {
    const [products, setProducts] = useState([]);
    const [courses, setCourses] = useState([]);
    const [user_email, setEmail] = useState("");
    const [coupon_code, setCoupon] = useState("");
    const [status, setStatus] = useState("paid");
    const [notes, setNotes] = useState("");
    const [items, setItems] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setEmail(""); setCoupon(""); setStatus("paid"); setNotes(""); setItems([]);
            api.get("/admin/products").then(({ data }) => setProducts(data));
            api.get("/admin/courses").then(({ data }) => setCourses(data));
        }
    }, [open]);

    const allOptions = useMemo(() => [
        ...products.map((p) => ({ id: p.id, kind: "product", name: p.name, price: p.price, image_url: p.image_url })),
        ...courses.map((c) => ({ id: c.id, kind: "course", name: c.title, price: c.price, image_url: c.image_url })),
    ], [products, courses]);

    const addItem = (opt) => {
        setItems((arr) => {
            const i = arr.findIndex((x) => x.id === opt.id && x.kind === opt.kind);
            if (i >= 0) return arr.map((x, k) => k === i ? { ...x, qty: x.qty + 1 } : x);
            return [...arr, { ...opt, qty: 1, mode: opt.kind === "course" ? "online" : undefined }];
        });
    };

    const updateQty = (idx, qty) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, qty: Math.max(1, Number(qty)) } : x));
    const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!user_email || items.length === 0) {
            toast.error("Email and at least one item are required");
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/orders", { user_email, items, status, coupon_code: coupon_code || undefined, notes });
            toast.success("Order created");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="order-create-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Create order (manual)</DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Customer email</Label>
                            <Input type="email" value={user_email} onChange={(e) => setEmail(e.target.value)} required placeholder="customer@example.com" className="mt-1.5 rounded-xl" data-testid="order-form-email" />
                            <p className="text-xs text-ink-700 mt-1">User must already exist.</p>
                        </div>
                        <div>
                            <Label>Status</Label>
                            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1.5 rounded-xl border border-input h-10 w-full px-3 text-sm bg-background">
                                {STATUSES.filter((s) => s !== "failed").map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>

                    <div>
                        <Label>Add item</Label>
                        <select onChange={(e) => { if (e.target.value) { addItem(JSON.parse(e.target.value)); e.target.value = ""; } }} defaultValue="" className="mt-1.5 rounded-xl border border-input h-10 w-full px-3 text-sm bg-background" data-testid="order-form-add-item">
                            <option value="">Pick a product or course…</option>
                            {allOptions.map((o) => (
                                <option key={`${o.kind}-${o.id}`} value={JSON.stringify(o)}>
                                    [{o.kind}] {o.name} — ₹{o.price}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-2">
                        {items.map((it, i) => (
                            <div key={i} className="flex items-center gap-3 bg-paper-alt rounded-xl p-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">[{it.kind}] {it.name}</div>
                                    <div className="text-xs text-ink-700">{fmtINR(it.price)} × {it.qty}</div>
                                </div>
                                <Input type="number" min={1} value={it.qty} onChange={(e) => updateQty(i, e.target.value)} className="w-20 rounded-xl" />
                                <Button type="button" size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => removeItem(i)}><X className="h-3.5 w-3.5" /></Button>
                            </div>
                        ))}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Coupon code (optional)</Label>
                            <Input value={coupon_code} onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="mt-1.5 rounded-xl uppercase" />
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="order-form-save">
                            {saving ? "Creating…" : "Create order"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function OrderDetailDialog({ open, onOpenChange, order, onUpdated }) {
    const [status, setStatus] = useState(order?.status || "");
    const [notes, setNotes] = useState(order?.notes || "");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (open && order) { setStatus(order.status); setNotes(order.notes || ""); }
    }, [open, order]);

    if (!order) return null;

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/orders/${order.id}`, { status, notes });
            toast.success("Order updated");
            onUpdated?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Order #{order.id.slice(0, 8)}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="rounded-2xl bg-paper-alt p-4 grid sm:grid-cols-2 gap-2 text-sm">
                        <div><span className="text-ink-700">Customer:</span> <span className="font-medium">{order.user_email}</span></div>
                        <div><span className="text-ink-700">Created:</span> {new Date(order.created_at).toLocaleString()}</div>
                        <div><span className="text-ink-700">Razorpay:</span> {order.razorpay_payment_id || "—"}</div>
                        <div><span className="text-ink-700">Coupon:</span> {order.coupon_code || "—"}</div>
                    </div>

                    <div>
                        <h4 className="font-semibold mb-2">Items</h4>
                        <div className="space-y-1.5">
                            {(order.items || []).map((it, i) => (
                                <div key={i} className="flex justify-between text-sm border-b border-gray-100 py-1.5">
                                    <div>
                                        <div className="font-medium">{it.name}</div>
                                        <div className="text-xs text-ink-700">[{it.kind}{it.mode ? ` · ${it.mode}` : ""}{it.variant_label ? ` · ${it.variant_label}` : ""}] qty {it.qty}</div>
                                    </div>
                                    <div className="font-medium">{fmtINR(it.price * it.qty)}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-2xl bg-white border border-gray-100 p-4 text-sm space-y-1">
                        <div className="flex justify-between"><span>Subtotal (excl. tax)</span><span>{fmtINR(order.subtotal ?? order.gross_subtotal)}</span></div>
                        {order.gst_amount > 0 && <div className="flex justify-between"><span>GST ({order.tax_mode || "inclusive"})</span><span>{fmtINR(order.gst_amount)}</span></div>}
                        {order.discount > 0 && <div className="flex justify-between text-accent-700"><span>Discount</span><span>−{fmtINR(order.discount)}</span></div>}
                        <div className="flex justify-between font-semibold border-t border-gray-100 pt-1.5"><span>Total</span><span>{fmtINR(order.total ?? order.subtotal)}</span></div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Status</Label>
                            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1.5 rounded-xl border border-input h-10 w-full px-3 text-sm bg-background" data-testid="order-detail-status">
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Close</Button>
                    <Button onClick={save} disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="order-detail-save">
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function OrdersTab() {
    const [items, setItems] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [activeOrder, setActiveOrder] = useState(null);

    const reload = () => api.get("/admin/orders").then(({ data }) => setItems(data));
    useEffect(() => { reload(); }, []);

    const remove = async (id) => {
        if (!confirm("Delete this order? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/orders/${id}`);
            toast.success("Order deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div data-testid="orders-tab">
            <div className="flex flex-wrap items-end justify-between mb-5 gap-3">
                <h2 className="font-heading text-2xl font-semibold">Orders ({items.length})</h2>
                <Button onClick={() => setCreateOpen(true)} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="order-new-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> Manual order
                </Button>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                    <thead className="bg-paper-alt text-ink-700 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Order</th>
                            <th className="text-left px-4 py-3">User</th>
                            <th className="text-left px-4 py-3">Items</th>
                            <th className="text-right px-4 py-3">Total</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-left px-4 py-3">Date</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((o) => (
                            <tr key={o.id} className="border-t border-gray-100 align-top hover:bg-paper-alt" data-testid={`admin-order-row-${o.id}`}>
                                <td className="px-4 py-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                                <td className="px-4 py-3 text-ink-700">{o.user_email}</td>
                                <td className="px-4 py-3">{o.items?.length || 0} item{(o.items?.length === 1) ? "" : "s"}</td>
                                <td className="px-4 py-3 text-right font-semibold text-brand-600">{fmtINR(o.total ?? o.subtotal)}</td>
                                <td className="px-4 py-3">
                                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide ${statusColor(o.status)}`}>{o.status}</span>
                                </td>
                                <td className="px-4 py-3 text-xs text-ink-700">{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <Button size="icon" variant="outline" className="rounded-full h-8 w-8 mr-1" onClick={() => setActiveOrder(o)} data-testid={`order-edit-${o.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                                    <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => remove(o.id)} data-testid={`order-delete-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-10 text-ink-700">No orders yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <CreateOrderDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={reload} />
            <OrderDetailDialog open={Boolean(activeOrder)} onOpenChange={(v) => !v && setActiveOrder(null)} order={activeOrder} onUpdated={reload} />
        </div>
    );
}
